/**
 * デザイン種別内訳 donut（四半期KPI カード横の独立カード）
 * 選択月における、このデザイナーの 4 種別（デザイン / 情報設計 / デザインチェック /
 * マスターデザインマージ）の実績pt 構成比を donut で表示する。塗り色はデザイナー主題色の濃淡。
 * 中央に合計（hover 時は当該種別）を、下に種別ごとの pt / 比率を凡例で並べる。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 選択月のデザイン種別別 実績pt 構成比 donut（種別ごとの pt と割合）
 * @graph-connects none
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pie, PieChart, ResponsiveContainer, Sector } from 'recharts';
import { formatNum } from '../combat-power/combat-power-utils.js';
import type { IdtCategory } from '../../../lib/api.js';

/** 種別ごとの pt スライス。 @graph-connects none */
export interface CategoryPtSlice {
  category: IdtCategory;
  pt: number;
  /** 塗り色（CSS color 値。デザイナー主題色の濃淡。例: color-mix(in srgb, var(--pt-accent) 70%, white)） */
  color: string;
}

/** @graph-connects none */
export function DesignCategoryDonut({ slices }: { slices: CategoryPtSlice[] }) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState<IdtCategory | null>(null);

  const total = slices.reduce((s, d) => s + d.pt, 0);
  const nonZero = slices.filter((s) => s.pt > 0);
  const focused = hovered ? (slices.find((s) => s.category === hovered) ?? null) : null;
  const focusedPct = focused && total > 0 ? ((focused.pt / total) * 100).toFixed(1) : null;

  if (total <= 0) {
    return (
      <div className="flex items-center justify-center w-full py-8 text-sm text-[var(--color-text-muted)]">
        {t('idtKpi.categoryDonut.empty', '種別pt がありません')}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-3 w-full">
      <div
        className="relative w-[180px] h-[180px] select-none"
        onMouseLeave={() => {
          setHovered(null);
        }}
      >
        <ResponsiveContainer>
          <PieChart>
            <Pie
              data={nonZero}
              dataKey="pt"
              nameKey="category"
              innerRadius={56}
              outerRadius={84}
              paddingAngle={1.2}
              startAngle={90}
              endAngle={-270}
              isAnimationActive
              animationDuration={900}
              animationEasing="ease-out"
              onMouseEnter={(payload) => {
                const cat = (payload as { category?: IdtCategory } | undefined)?.category;
                if (cat) setHovered(cat);
              }}
              onMouseLeave={() => {
                setHovered(null);
              }}
              style={{ cursor: 'default', outline: 'none' }}
              tabIndex={-1}
              shape={(props: unknown) => {
                const p = props as {
                  cx: number;
                  cy: number;
                  innerRadius: number;
                  outerRadius: number;
                  startAngle: number;
                  endAngle: number;
                  payload: CategoryPtSlice;
                };
                const isHovered = hovered === p.payload.category;
                const isMuted = hovered !== null && !isHovered;
                return (
                  <g
                    style={{
                      transform: isHovered ? 'scale(1.06)' : 'scale(1)',
                      transformOrigin: `${String(p.cx)}px ${String(p.cy)}px`,
                      transition: 'transform 0.5s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease',
                      opacity: isMuted ? 0.2 : 1,
                    }}
                  >
                    <Sector
                      cx={p.cx}
                      cy={p.cy}
                      innerRadius={p.innerRadius}
                      outerRadius={p.outerRadius}
                      startAngle={p.startAngle}
                      endAngle={p.endAngle}
                      fill={p.payload.color}
                      stroke="var(--color-bg-surface)"
                      strokeWidth={2}
                      style={{ outline: 'none', vectorEffect: 'non-scaling-stroke' }}
                    />
                  </g>
                );
              }}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
          <div
            className="text-[10px] tracking-wider transition-colors duration-300"
            style={{
              color: focused ? focused.color : 'var(--color-text-muted)',
            }}
          >
            {focused ? focused.category : t('idtKpi.categoryDonut.totalLabel', '合計')}
          </div>
          <div
            className="text-2xl font-black tabular-nums tracking-tight mt-0.5 transition-colors duration-300"
            style={{
              color: focused ? focused.color : 'var(--color-text-primary)',
            }}
          >
            {formatNum(focused ? focused.pt : total, 1)}
          </div>
          <div className="text-[10px] text-[var(--color-text-muted)] tabular-nums">
            {focusedPct ? `${focusedPct}%` : 'pt'}
          </div>
        </div>
      </div>

      {/* 凡例: 種別ごとの pt と割合 */}
      <ul className="w-full space-y-1">
        {slices.map((s) => {
          const pct = total > 0 ? (s.pt / total) * 100 : 0;
          return (
            <li
              key={s.category}
              className="flex items-center justify-between gap-2 text-[11px]"
              onMouseEnter={() => {
                setHovered(s.category);
              }}
              onMouseLeave={() => {
                setHovered(null);
              }}
            >
              <span className="flex items-center gap-1.5 min-w-0">
                <span
                  className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                  style={{ background: s.color }}
                />
                <span className="truncate text-[var(--color-text-secondary)]">{s.category}</span>
              </span>
              <span className="tabular-nums text-[var(--color-text-muted)] whitespace-nowrap">
                {formatNum(s.pt, 1)}
                <span className="ml-1 opacity-70">({pct.toFixed(0)}%)</span>
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
